/**
 * Relative time formatting for the runs list/detail views.
 */

import type { AgentRun } from '../types/agentRun.types';

const DIVISIONS: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: 'seconds' as Intl.RelativeTimeFormatUnit },
  { amount: 60, unit: 'minutes' as Intl.RelativeTimeFormatUnit },
  { amount: 24, unit: 'hours' as Intl.RelativeTimeFormatUnit },
  { amount: 7, unit: 'days' as Intl.RelativeTimeFormatUnit },
  { amount: 4.34524, unit: 'weeks' as Intl.RelativeTimeFormatUnit },
  { amount: 12, unit: 'months' as Intl.RelativeTimeFormatUnit },
  { amount: Number.POSITIVE_INFINITY, unit: 'years' as Intl.RelativeTimeFormatUnit },
];

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/** e.g. "3 minutes ago", "an hour ago". `now` is injectable for tests. */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  let duration = (new Date(iso).getTime() - now) / 1000;

  for (const division of DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return rtf.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return rtf.format(0, 'seconds');
}

const JUST_NOW_MS = 60_000;

/**
 * How long ago a past event was, for the panel's "by <name> <age>" lines:
 * "just now" under a minute, and also for a time ahead of the browser's
 * clock (a server a few seconds fast), where `formatRelativeTime` would
 * say "now" or "in 3 seconds". `now` is injectable for tests.
 */
export function formatAgo(iso: string, now: number = Date.now()): string {
  if (now - new Date(iso).getTime() < JUST_NOW_MS) return 'just now';
  return formatRelativeTime(iso, now);
}

/**
 * "started" label for a run row. A run without `started_at` has not begun
 * executing yet (still `pending`), so this says "queued" against
 * `created_at` rather than implying a start time that does not exist.
 */
export function runStartLabel(run: Pick<AgentRun, 'started_at' | 'created_at'>): string {
  if (run.started_at != null) {
    return `started ${formatRelativeTime(run.started_at)}`;
  }
  return `queued ${formatRelativeTime(run.created_at)}`;
}

/**
 * `budget_seconds` as a compact duration, e.g. "45m" or "1h 30m".
 *
 * This is a LIMIT the run was allowed to run for, not how long it
 * actually ran — nothing today tracks that. Never present this as
 * elapsed time or "used".
 */
export function formatBudgetSeconds(seconds: number): string {
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) return `${String(totalMinutes)}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${String(hours)}h ${String(minutes)}m` : `${String(hours)}h`;
}
